import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { adminClient, formatAdminDate } from "@/api/adminClient";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AdminListPager, AdminListToolbar } from "@/components/admin/AdminListToolbar";
import { useAdminListQuery } from "@/components/admin/useAdminListQuery";

const PAGE_SIZE = 50;

function deliveryNotice(result) {
  const invitation = result?.invitation ?? result ?? {};
  if (invitation.email_status === 'failed') return invitation.email_error || 'Invitation saved, but delivery failed.';
  if (invitation.email_status === 'disabled') return 'Invitation saved, but email delivery is disabled.';
  if (invitation.email_status === 'accepted') return 'Invitation saved. Email accepted by provider; delivery is not confirmed.';
  return 'Invitation saved; delivery status is unknown.';
}

export default function AdminStaff() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [state, setState] = useState("loading");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [selected, setSelected] = useState(null);
  const [reason, setReason] = useState("");
  const [expires, setExpires] = useState("");
  const [busy, setBusy] = useState(null);
  const [dialogError, setDialogError] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [addDestination, setAddDestination] = useState("provider");
  const [addEmail, setAddEmail] = useState("");
  const [addRole, setAddRole] = useState("member");
  const [addOrganisation, setAddOrganisation] = useState("");
  const [customerSearch, setCustomerSearch] = useState("");
  const [customers, setCustomers] = useState([]);
  const [organisationSearch, setOrganisationSearch] = useState('');
  const [filterOrganisations, setFilterOrganisations] = useState([]);
  const [accessPolicy, setAccessPolicy] = useState(null);
  const [addMethod, setAddMethod] = useState('verified_email_password');
  const [addProvider, setAddProvider] = useState('');
  const [addGuest, setAddGuest] = useState(false);
  const [addGuestReason, setAddGuestReason] = useState('');
  const [addError, setAddError] = useState("");
  const [staffTab, setStaffTab] = useState('users');
  const [invitations, setInvitations] = useState([]);
  const generation = useRef(0);
  const customerGeneration = useRef(0);
  const policyGeneration = useRef(0);
  const organisationFilterGeneration = useRef(0);
  const mutationGeneration = useRef(0);
  const mounted = useRef(false);
  const busyRef = useRef(false);
  const lastTrigger = useRef(null);
  const controlsDisabled = state !== "ready" || !!busy;
  const { query, update, reset } = useAdminListQuery();
  const page = Math.floor(Number(query.offset || 0) / PAGE_SIZE);
  async function load(nextPage = page, tab = staffTab) {
    const current = ++generation.current;
    setState("loading");
    setError("");
    try {
      const common = `paged=true&limit=${PAGE_SIZE}&offset=${nextPage * PAGE_SIZE}${query.q ? `&q=${encodeURIComponent(query.q)}` : ""}${query.scope ? `&scope=${encodeURIComponent(query.scope)}` : ""}${query.role ? `&role=${encodeURIComponent(query.role)}` : ""}${query.organisation_id ? `&organisation_id=${encodeURIComponent(query.organisation_id)}` : ""}`;
      const result = await adminClient(tab === 'users'
        ? `/admin/staff?${common}${query.status ? `&status=${encodeURIComponent(query.status)}` : ''}`
        : `/admin/staff/invitations?${common}${query.delivery ? `&delivery=${encodeURIComponent(query.delivery)}` : ''}`);
      if (!mounted.current || current !== generation.current) return;
      setRows(result.items || result.users || []); setInvitations(result.items || []);
      setTotal(Number(result.total) || 0);
      setState("ready");
    } catch (e) {
      if (!mounted.current || current !== generation.current) return;
      setError(e.message);
      setState("error");
    }
  }
  useEffect(() => {
    mounted.current = true;
    load(page);
    return () => {
      mounted.current = false;
      generation.current += 1;
      mutationGeneration.current += 1;
      busyRef.current = false;
    };
  }, [query.q, query.scope, query.status, query.role, query.organisation_id, query.delivery, query.offset, staffTab]);
  async function invitationAction(invitation, action) {
    setBusy(`${action}:${invitation.id}`); setError(''); setNotice('');
    try {
      const result = await adminClient(`/orgs/${encodeURIComponent(invitation.organisation_id)}/invitations/${encodeURIComponent(invitation.id)}${action === 'resend' ? '/resend' : ''}`, { method: action === 'resend' ? 'POST' : 'DELETE' });
      await load(page, 'invitations');
      setNotice(action === 'resend' ? deliveryNotice(result) : 'Invitation cancelled.');
    } catch (e) { setError(e.message); } finally { setBusy(null); }
  }
  useEffect(() => {
    const current = ++organisationFilterGeneration.current;
    Promise.all([
      adminClient('/admin/provider'),
      adminClient(`/admin/customers?paged=true&status=active&limit=100&sort=name&direction=asc${organisationSearch ? `&q=${encodeURIComponent(organisationSearch)}` : ''}`),
    ]).then(([provider, result]) => {
      if (current !== organisationFilterGeneration.current) return;
      setFilterOrganisations([provider, ...(result.items || [])]);
    }).catch((e) => { if (current === organisationFilterGeneration.current) setError(e.message); });
    return () => { if (current === organisationFilterGeneration.current) organisationFilterGeneration.current += 1; };
  }, [organisationSearch]);
  useEffect(() => {
    if (!addOpen || addDestination !== "customer") { setCustomers([]); return; }
    const current = ++customerGeneration.current;
    setCustomers([]); setAddError('');
    adminClient(`/admin/customers?paged=true&status=active&limit=100&sort=name&direction=asc${customerSearch ? `&q=${encodeURIComponent(customerSearch)}` : ''}`)
      .then((result) => { if (current === customerGeneration.current) setCustomers(result.items || []); })
      .catch((e) => { if (current === customerGeneration.current) setAddError(e.message); });
    return () => { if (current === customerGeneration.current) customerGeneration.current += 1; };
  }, [addOpen, addDestination, customerSearch]);
  useEffect(() => {
    if (!addOpen) return;
    const current = ++policyGeneration.current;
    setAccessPolicy(null); setAddError('');
    setAddMethod('verified_email_password'); setAddProvider(''); setAddGuest(false); setAddGuestReason('');
    if (addDestination === 'customer' && !addOrganisation) return;
    const target = addDestination === 'customer' ? Promise.resolve(addOrganisation) : adminClient('/admin/provider').then((row) => row.id);
    target.then((id) => adminClient(`/orgs/${encodeURIComponent(id)}/access-policy`))
      .then((policy) => { if (current === policyGeneration.current) setAccessPolicy(policy); })
      .catch((e) => { if (current === policyGeneration.current) setAddError(e.message); });
    return () => { if (current === policyGeneration.current) policyGeneration.current += 1; };
  }, [addOpen, addDestination, addOrganisation]);
  async function submitAdd(event) {
    event.preventDefault();
    if (!addEmail.trim() || (addDestination === 'customer' && !addOrganisation)) return;
    setBusy('invite'); setAddError(''); setNotice('');
    try {
      const result = await adminClient('/admin/staff/invitations', { method: 'POST', body: { destination: addDestination, email: addEmail.trim(), role: addRole, authentication_method: addMethod, ...(addMethod === 'organisation_sso' ? { provider_id: addProvider } : {}), is_guest: addGuest, ...(addGuest ? { guest_reason: addGuestReason } : {}), ...(addDestination === 'customer' ? { organisation_id: addOrganisation } : {}) } });
      setAddOpen(false); setAddEmail(''); setAddRole('member'); setAddOrganisation('');
      update({ offset: '' }, { resetOffset: false }); setStaffTab('invitations');
      await load(0, 'invitations');
      setNotice(deliveryNotice(result));
    } catch (e) { setAddError(e.message); } finally { setBusy(null); }
  }
  function openBan(row, event) {
    if (busyRef.current || state !== "ready") return;
    lastTrigger.current = event.currentTarget;
    setSelected(row);
    setReason("");
    setExpires("");
    setDialogError("");
    setError("");
  }
  async function submitBan(e) {
    e.preventDefault();
    const target = selected;
    if (!target || busyRef.current || !mounted.current || state !== "ready")
      return;
    const currentMutation = ++mutationGeneration.current;
    busyRef.current = true;
    setBusy(target.id);
    setDialogError("");
    setError("");
    try {
      const committed = await adminClient(`/admin/staff/${target.id}/ban`, {
        method: "POST",
        body: {
          reason,
          ...(expires
            ? { ban_expires_at: new Date(expires).toISOString() }
            : {}),
        },
      });
      if (!mounted.current || currentMutation !== mutationGeneration.current)
        return;
      setRows((old) =>
        old.map((item) => (item.id === committed.id ? committed : item)),
      );
      setDialogError("");
      setSelected(null);
      await load(page);
    } catch (err) {
      if (mounted.current && currentMutation === mutationGeneration.current)
        setDialogError(err.message);
    } finally {
      if (currentMutation === mutationGeneration.current) {
        busyRef.current = false;
        if (mounted.current) setBusy(null);
      }
    }
  }
  async function unban(row) {
    if (busyRef.current || !mounted.current || state !== "ready") return;
    const currentMutation = ++mutationGeneration.current;
    busyRef.current = true;
    setBusy(row.id);
    setError("");
    try {
      const committed = await adminClient(`/admin/staff/${row.id}/unban`, {
        method: "POST",
        body: {},
      });
      if (!mounted.current || currentMutation !== mutationGeneration.current)
        return;
      setRows((old) =>
        old.map((item) => (item.id === committed.id ? committed : item)),
      );
      await load(page);
    } catch (e) {
      if (mounted.current && currentMutation === mutationGeneration.current)
        setError(e.message);
    } finally {
      if (currentMutation === mutationGeneration.current) {
        busyRef.current = false;
        if (mounted.current) setBusy(null);
      }
    }
  }
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-foreground">
            Staff &amp; access
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Review accounts and revoke access through the audited staff
            controls.
          </p>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            To add staff, have the person sign up with an approved staff-domain
            email address, then grant or review their provider membership here.
            Local development uses the configured staff allow-list; this page
            does not create passwords or bypass that sign-up control.
          </p>
        </div>
        <div className="flex gap-2"><button type="button" onClick={() => { setAddError(''); setAddOpen(true); }} disabled={state === "loading" || !!busy} className="min-h-11 rounded-lg bg-teal-700 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50">Add user</button><button type="button" onClick={() => load(page)} disabled={state === "loading" || !!busy} className="min-h-11 rounded-lg border border-border px-3 py-2 text-sm font-semibold hover:bg-muted disabled:opacity-50">Refresh</button></div>
      </div>
      <AdminListToolbar search={query.q || ""} searchLabel="Search staff name or email" total={total} loading={state === "loading"} onSearch={(q) => update({ q })} onReset={reset}>
        <label className="text-sm font-medium">Scope<select aria-label="Filter staff scope" value={query.scope || "all"} onChange={(event) => update({ scope: event.target.value })} className="mt-1 block min-h-11 rounded-lg border border-input bg-background px-3 py-2 text-sm"><option value="all">All users</option><option value="staff">Provider staff</option><option value="customer">Customer users</option></select></label>
        <label className="text-sm font-medium">Status<select aria-label="Filter staff status" value={query.status || "all"} onChange={(event) => update({ status: event.target.value })} className="mt-1 block min-h-11 rounded-lg border border-input bg-background px-3 py-2 text-sm"><option value="all">All statuses</option><option value="active">Active</option><option value="banned">Banned</option></select></label>
        <label className="text-sm font-medium">Role<select aria-label="Filter staff role" value={query.role || "all"} onChange={(event) => update({ role: event.target.value })} className="mt-1 block min-h-11 rounded-lg border border-input bg-background px-3 py-2 text-sm"><option value="all">All roles</option><option value="owner">Owner</option><option value="admin">Admin</option><option value="member">Member</option></select></label>
        <label className="text-sm font-medium">Find organisation<input aria-label="Find organisation for filter" value={organisationSearch} onChange={(event) => setOrganisationSearch(event.target.value)} placeholder="Search provider or customers" className="mt-1 block min-h-11 rounded-lg border border-input bg-background px-3 py-2 text-sm" /></label>
        <label className="text-sm font-medium">Organisation<select aria-label="Filter staff organisation" value={query.organisation_id || "all"} onChange={(event) => update({ organisation_id: event.target.value })} className="mt-1 block min-h-11 rounded-lg border border-input bg-background px-3 py-2 text-sm"><option value="all">All organisations</option>{filterOrganisations.map((organisation) => <option key={organisation.id} value={organisation.id}>{organisation.name}</option>)}</select></label>
        {staffTab === 'invitations' && <label className="text-sm font-medium">Delivery<select aria-label="Filter invitation delivery" value={query.delivery || "all"} onChange={(event) => update({ delivery: event.target.value })} className="mt-1 block min-h-11 rounded-lg border border-input bg-background px-3 py-2 text-sm"><option value="all">All delivery states</option><option value="accepted">Accepted</option><option value="failed">Failed</option><option value="disabled">Disabled</option><option value="unknown">Unknown</option></select></label>}
      </AdminListToolbar>
      <div className="flex gap-2" role="tablist" aria-label="Staff access records"><button type="button" role="tab" aria-selected={staffTab === 'users'} onClick={() => setStaffTab('users')} className={`min-h-11 rounded-lg px-3 py-2 text-sm font-semibold ${staffTab === 'users' ? 'bg-teal-700 text-white' : 'border border-border'}`}>DI staff & customer users</button><button type="button" role="tab" aria-selected={staffTab === 'invitations'} onClick={() => setStaffTab('invitations')} className={`min-h-11 rounded-lg px-3 py-2 text-sm font-semibold ${staffTab === 'invitations' ? 'bg-teal-700 text-white' : 'border border-border'}`}>Pending invitations</button></div>
      {notice && <p className="rounded-lg border border-teal-200 bg-teal-50 p-4 text-sm text-teal-900" role="status">{notice}</p>}
      {error && (
        <p
          className="rounded-lg border border-destructive/40 bg-card p-4 text-sm text-destructive"
          role="alert"
        >
          {error}{" "}
          <button
            type="button"
            onClick={() => load(page)}
            disabled={!!busy}
            className="ml-2 underline disabled:opacity-50"
          >
            Retry GET
          </button>
        </p>
      )}
      {state === "loading" && (
        <p
          role="status"
          className="rounded-lg border border-border bg-card p-5 text-sm text-muted-foreground"
        >
          Loading staff directory…
        </p>
      )}
      {staffTab === 'users' && rows.length > 0 && state !== "idle" && (
        <div
          role="region"
          aria-label="Staff directory table"
          tabIndex={0}
          className="relative overflow-x-auto rounded-xl border border-border bg-card shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
        >
          <table className="w-full min-w-[980px] text-left text-sm">
            <caption className="sr-only">Staff and access directory</caption>
            <thead>
              <tr className="border-b border-border text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-4 py-3">Person</th>
                <th className="px-4 py-3">Memberships</th>
                <th className="px-4 py-3">Last sign-in</th>
                <th className="px-4 py-3">Status / reason</th>
                <th className="px-4 py-3">History</th>
                <th className="px-4 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.id}
                  className="border-b border-border align-top last:border-0"
                >
                  <th scope="row" className="px-4 py-3 font-semibold">
                    {row.name}
                    <span className="mt-1 block text-xs font-normal text-muted-foreground">
                      {row.email}
                    </span>
                  </th>
                  <td className="px-4 py-3">
                    {row.memberships?.length ? (
                      <ul className="space-y-1">
                        {row.memberships.map((membership) => (
                          <li
                            key={`${membership.organisation_id}-${membership.role}`}
                            className="text-xs"
                          >
                            {membership.organisation_name} · {membership.role}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <span className="text-muted-foreground">
                        No memberships
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {formatAdminDate(row.last_sign_in_at)}
                  </td>
                  <td className="px-4 py-3">
                    {row.banned ? (
                      <>
                        <span className="rounded-full border border-red-200 bg-red-50 px-2 py-1 text-xs font-semibold text-red-800">
                          Banned
                        </span>
                        {row.ban_reason && (
                          <p className="mt-2 max-w-xs text-xs text-muted-foreground">
                            {row.ban_reason}
                          </p>
                        )}
                        {row.ban_expires_at && (
                          <p className="mt-1 text-xs text-muted-foreground">
                            Until {formatAdminDate(row.ban_expires_at)}
                          </p>
                        )}
                      </>
                    ) : (
                      <span className="rounded-full border border-teal-200 bg-teal-50 px-2 py-1 text-xs font-semibold text-teal-800">
                        Active
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <Link
                      to="/admin/audit?action=staff.user.ban"
                      className="font-semibold text-teal-800 underline decoration-teal-300 underline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
                    >
                      Ban audit<span className="sr-only"> for {row.name}</span>
                    </Link>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Operator IP history is in Audit.
                    </p>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {row.banned ? (
                      <button
                        type="button"
                        onClick={() => unban(row)}
                        disabled={controlsDisabled}
                        className="min-h-11 rounded-lg border border-border px-3 py-2 text-xs font-semibold hover:bg-muted disabled:opacity-50"
                      >
                        Unban
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={(event) => openBan(row, event)}
                        disabled={controlsDisabled}
                        className="min-h-11 rounded-lg border border-red-300 px-3 py-2 text-xs font-semibold text-red-800 hover:bg-red-50 disabled:opacity-50"
                      >
                        Ban
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {staffTab === 'users' && state === "ready" && rows.length === 0 && (
        <p className="rounded-lg border border-dashed border-border bg-card p-8 text-center text-sm text-muted-foreground">
          No users found.
        </p>
      )}
      {staffTab === 'invitations' && state === 'ready' && <div className="rounded-xl border border-border bg-card"><ul aria-label="Pending invitations" className="divide-y divide-border">{invitations.map((invitation) => <li key={invitation.id} className="flex flex-wrap items-center justify-between gap-3 p-4"><div><p className="font-semibold">{invitation.email}</p><p className="text-sm text-muted-foreground">{invitation.organisation_name} · {invitation.role} · delivery {invitation.email_status}</p></div><div className="flex gap-2"><button type="button" disabled={!!busy} onClick={() => invitationAction(invitation, 'resend')} className="min-h-11 rounded-lg border border-border px-3 py-2 text-sm font-semibold">Resend</button><button type="button" disabled={!!busy} onClick={() => invitationAction(invitation, 'cancel')} className="min-h-11 rounded-lg border border-border px-3 py-2 text-sm font-semibold text-red-700 dark:text-red-300">Cancel</button></div></li>)}</ul>{invitations.length === 0 && <p className="p-6 text-center text-sm text-muted-foreground">No pending invitations found.</p>}</div>}
      <AdminListPager offset={page * PAGE_SIZE} limit={PAGE_SIZE} total={total} disabled={controlsDisabled} onPage={(offset) => update({ offset }, { resetOffset: false })} />
      <Dialog
        open={!!selected}
        onOpenChange={(open) => {
          if (!open && !busyRef.current) setSelected(null);
        }}
      >
        <DialogContent
          className="admin-console-dialog"
          onEscapeKeyDown={(event) => {
            if (busy) event.preventDefault();
          }}
          onPointerDownOutside={(event) => {
            if (busy) event.preventDefault();
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            lastTrigger.current?.focus();
          }}
        >
          <DialogHeader>
            <DialogTitle>Ban {selected?.name}?</DialogTitle>
            <DialogDescription>
              All of this account’s sessions will be revoked. The reason and
              operator IP are recorded in Audit.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitBan}>
            <label
              className="mt-4 block text-sm font-semibold"
              htmlFor="ban-reason"
            >
              Reason
              <input
                id="ban-reason"
                required
                minLength={10}
                maxLength={500}
                disabled={!!busy}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="mt-1 min-h-11 w-full rounded-lg border border-border bg-background px-3 py-2 font-normal"
              />
            </label>
            <label
              className="mt-4 block text-sm font-semibold"
              htmlFor="ban-expires"
            >
              Expires (optional)
              <input
                id="ban-expires"
                type="datetime-local"
                disabled={!!busy}
                value={expires}
                onChange={(e) => setExpires(e.target.value)}
                className="mt-1 min-h-11 w-full rounded-lg border border-border bg-background px-3 py-2 font-normal"
              />
            </label>
            {dialogError && (
              <p className="mt-3 text-sm text-destructive" role="alert">
                {dialogError}
              </p>
            )}
            <DialogFooter className="mt-5">
              <button
                type="button"
                onClick={() => setSelected(null)}
                disabled={!!busy}
                className="min-h-11 rounded-lg border border-border px-3 py-2 text-sm font-semibold hover:bg-muted disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!!busy || !selected}
                className="min-h-11 rounded-lg bg-red-700 px-3 py-2 text-sm font-semibold text-white hover:bg-red-800 disabled:opacity-50"
              >
                {busy ? "Banning…" : "Confirm ban"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={addOpen} onOpenChange={(open) => { if (!busy) setAddOpen(open); }}>
        <DialogContent aria-describedby="add-user-description" className="max-h-[calc(100vh-2rem)] overflow-y-auto">
          <DialogHeader><DialogTitle>Add user</DialogTitle><DialogDescription id="add-user-description">Send an invitation. The recipient creates their own identity and password if required; existing accounts receive a new organisation invitation without duplication.</DialogDescription></DialogHeader>
          <form onSubmit={submitAdd} className="space-y-4">
            <fieldset><legend className="text-sm font-medium">Destination</legend><div className="mt-2 grid gap-2"><label className="flex gap-2 text-sm"><input type="radio" checked={addDestination === 'provider'} onChange={() => { setAddOrganisation(''); setAddDestination('provider'); }} />DI staff</label><p className="text-xs text-muted-foreground">DI membership grants access to this provider console.</p><label className="flex gap-2 text-sm"><input type="radio" checked={addDestination === 'customer'} onChange={() => setAddDestination('customer')} />Customer user</label></div></fieldset>
            {addDestination === 'customer' && <><label className="block text-sm font-medium">Find customer<input value={customerSearch} onChange={(event) => setCustomerSearch(event.target.value)} placeholder="Search active customers" className="mt-1 block min-h-11 w-full rounded-lg border border-input bg-background px-3 py-2" /></label><label className="block text-sm font-medium">Customer<select required value={addOrganisation} onChange={(event) => setAddOrganisation(event.target.value)} className="mt-1 block min-h-11 w-full rounded-lg border border-input bg-background px-3 py-2"><option value="">Choose one active customer</option>{customers.map((customer) => <option key={customer.id} value={customer.id}>{customer.name}</option>)}</select></label></>}
            <label className="block text-sm font-medium">Email address<input required type="email" value={addEmail} onChange={(event) => setAddEmail(event.target.value)} className="mt-1 block min-h-11 w-full rounded-lg border border-input bg-background px-3 py-2" /></label>
            <label className="block text-sm font-medium">Role<select value={addRole} onChange={(event) => setAddRole(event.target.value)} className="mt-1 block min-h-11 w-full rounded-lg border border-input bg-background px-3 py-2"><option value="member">Member</option><option value="admin">Admin</option>{addDestination === 'provider' && <option value="owner">Owner</option>}</select></label>
            <label className="block text-sm font-medium">Required sign-in<select value={addMethod} onChange={(event) => { setAddMethod(event.target.value); setAddProvider(''); }} className="mt-1 block min-h-11 w-full rounded-lg border border-input bg-background px-3 py-2"><option value="verified_email_password">Verified email and password</option>{(accessPolicy?.sign_in_methods || []).some((method) => method.authentication_method === 'organisation_sso' && method.available) && <option value="organisation_sso">Organisation SSO</option>}</select></label>
            {addMethod === 'organisation_sso' && <label className="block text-sm font-medium">SSO provider<select required value={addProvider} onChange={(event) => setAddProvider(event.target.value)} className="mt-1 block min-h-11 w-full rounded-lg border border-input bg-background px-3 py-2"><option value="">Choose provider</option>{(accessPolicy?.sign_in_methods || []).filter((method) => method.authentication_method === 'organisation_sso' && method.available).map((method) => <option key={method.provider_id} value={method.provider_id}>{method.label}</option>)}</select></label>}
            {accessPolicy?.guests_enabled && <><label className="flex gap-2 text-sm"><input type="checkbox" checked={addGuest} onChange={(event) => setAddGuest(event.target.checked)} />Approve individual guest exception</label>{addGuest && <label className="block text-sm font-medium">Guest approval reason<textarea required minLength={10} maxLength={500} value={addGuestReason} onChange={(event) => setAddGuestReason(event.target.value)} className="mt-1 block w-full rounded-lg border border-input bg-background px-3 py-2" /></label>}</>}
            {addDestination === 'provider' && <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900">Provider membership grants console access. Member is the least-privileged default.</p>}
            {addError && <p role="alert" className="text-sm text-destructive">{addError}</p>}
            <DialogFooter><button type="button" onClick={() => setAddOpen(false)} disabled={!!busy} className="min-h-11 rounded-lg border border-border px-4 py-2">Cancel</button><button type="submit" disabled={!!busy || !accessPolicy || !addEmail.trim() || (addDestination === 'customer' && !addOrganisation) || (addMethod === 'organisation_sso' && !addProvider) || (addGuest && addGuestReason.trim().length < 10)} className="min-h-11 rounded-lg bg-teal-700 px-4 py-2 font-semibold text-white disabled:opacity-50">{busy === 'invite' ? 'Sending…' : 'Send invitation'}</button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
